import { useState, useEffect } from "react";
import api from "../../../../api/axios";
import { useCurrentTenant } from "../../../../hooks/useCurrentTenant";
import { FiTrash2, FiX, FiArrowUp, FiArrowDown, FiCheck } from "react-icons/fi";
import "./AttributeDefaultsSettings.css";

const AVAILABLE_LEVELS = [
    { value: "recordType", label: "Record Type" },
    { value: "name", label: "Product Group" },
    { value: "type", label: "Type" },
    { value: "manufacturerShortName", label: "Manufacturer Short Name" },
    { value: "manufacturer", label: "Manufacturer" },
];

const AttributeDefaultsSettings = ({ isOpen, onClose }) => {
    const currentTenantId = useCurrentTenant();
    const [defaults, setDefaults] = useState([]);
    const [newKey, setNewKey] = useState("");

    const [hierarchies, setHierarchies] = useState([]);
    const [newHierarchyName, setNewHierarchyName] = useState("");
    const [newHierarchyLevels, setNewHierarchyLevels] = useState([]);
    const [levelToAdd, setLevelToAdd] = useState("");

    useEffect(() => {
        if (isOpen) {
            fetchDefaults();
            fetchHierarchies();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, currentTenantId]);

    const fetchDefaults = async () => {
        try {
            const { data } = await api.get("/tenantattributedefault", {
                headers: { "X-Tenant": currentTenantId },
            });
            setDefaults(data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleAdd = async () => {
        const key = newKey.trim();
        if (!key) return;

        try {
            await api.post("/tenantattributedefault", JSON.stringify(key), {
                headers: {
                    "X-Tenant": currentTenantId,
                    "Content-Type": "application/json",
                },
            });
            setNewKey("");
            fetchDefaults();
        } catch (err) {
            console.error("Failed to add default attribute", err);
        }
    };

    const handleDelete = async (id) => {
        try {
            await api.delete(`/tenantattributedefault/${id}`, {
                headers: { "X-Tenant": currentTenantId },
            });
            fetchDefaults();
        } catch (err) {
            console.error(err);
        }
    };

    // ---- Cascading filter hierarchies ----

    const fetchHierarchies = async () => {
        try {
            const { data } = await api.get("/tenantfilterhierarchy", {
                headers: { "X-Tenant": currentTenantId },
            });
            setHierarchies(data);
        } catch (err) {
            console.error(err);
        }
    };

    const handleAddLevel = () => {
        if (!levelToAdd || newHierarchyLevels.includes(levelToAdd)) return;
        setNewHierarchyLevels((prev) => [...prev, levelToAdd]);
        setLevelToAdd("");
    };

    const handleRemoveLevel = (level) => {
        setNewHierarchyLevels((prev) => prev.filter((l) => l !== level));
    };

    const handleMoveLevel = (index, direction) => {
        setNewHierarchyLevels((prev) => {
            const next = [...prev];
            const target = index + direction;
            if (target < 0 || target >= next.length) return prev;
            [next[index], next[target]] = [next[target], next[index]];
            return next;
        });
    };

    const handleCreateHierarchy = async () => {
        const name = newHierarchyName.trim();
        if (!name || newHierarchyLevels.length === 0) return;

        const key = name.toLowerCase().replace(/\s+/g, "-");

        try {
            await api.post(
                "/tenantfilterhierarchy",
                { key, name, levels: newHierarchyLevels },
                { headers: { "X-Tenant": currentTenantId } }
            );
            setNewHierarchyName("");
            setNewHierarchyLevels([]);
            fetchHierarchies();
        } catch (err) {
            console.error("Failed to create hierarchy", err);
        }
    };

    const handleActivateHierarchy = async (id) => {
        try {
            await api.post(`/tenantfilterhierarchy/${id}/activate`, null, {
                headers: { "X-Tenant": currentTenantId },
            });
            fetchHierarchies();
        } catch (err) {
            console.error("Failed to activate hierarchy", err);
        }
    };

    const handleDeleteHierarchy = async (id) => {
        try {
            await api.delete(`/tenantfilterhierarchy/${id}`, {
                headers: { "X-Tenant": currentTenantId },
            });
            fetchHierarchies();
        } catch (err) {
            console.error(err);
        }
    };

    const levelLabel = (value) =>
        AVAILABLE_LEVELS.find((l) => l.value === value)?.label ?? value;

    if (!isOpen) return null;

    return (
        <div className="attr-defaults-overlay" onClick={onClose}>
            <div className="attr-defaults" onClick={(e) => e.stopPropagation()}>

                <div className="attr-defaults-header">
                    <h3>Default Article Attributes</h3>
                    <button className="attr-defaults-close" onClick={onClose}>
                        <FiX />
                    </button>
                </div>

                <p className="attr-defaults-hint">
                    These fields are automatically added to every new article — created,
                    imported from the global library, or bulk-imported.
                </p>

                <div className="attr-defaults-add">
                    <input
                        type="text"
                        placeholder="e.g. ERP Number"
                        value={newKey}
                        onChange={(e) => setNewKey(e.target.value)}
                        onKeyDown={(e) => e.key === "Enter" && handleAdd()}
                    />
                    <button onClick={handleAdd}>Add</button>
                </div>

                <ul className="attr-defaults-list">
                    {defaults.length === 0 && (
                        <li className="attr-defaults-empty">No default attributes yet.</li>
                    )}

                    {defaults.map((d) => (
                        <li key={d.id}>
                            <span>{d.key}</span>
                            <button onClick={() => handleDelete(d.id)}>
                                <FiTrash2 />
                            </button>
                        </li>
                    ))}
                </ul>

                <hr className="attr-defaults-divider" />
                <div className="herarchy-header">

                    <h3>Cascading Filter Hierarchies</h3>

                </div>
                <p className="attr-defaults-hint">
                    Choose which hierarchy the Article Tree groups by, in order.
                    The active hierarchy applies for everyone on this tenant.
                </p>

                <ul className="attr-defaults-list hierarchy-list">
                    {hierarchies.map((h) => (
                        <li key={h.id} className="hierarchy-row">
                            <div className="hierarchy-row-main">
                                <button
                                    className={`hierarchy-activate ${h.isActive ? "active" : ""}`}
                                    onClick={() => !h.isActive && handleActivateHierarchy(h.id)}
                                    title={h.isActive ? "Active" : "Set as active"}
                                >
                                    {h.isActive && <FiCheck />}
                                </button>
                                <span className="hierarchy-name">{h.name}</span>
                                <span className="hierarchy-levels">
                                    {h.levels.map(levelLabel).join(" → ")}
                                </span>
                            </div>

                            {h.key !== "default" && (
                                <button onClick={() => handleDeleteHierarchy(h.id)}>
                                    <FiTrash2 />
                                </button>
                            )}
                        </li>
                    ))}
                </ul>

                <div className="hierarchy-builder">
                    <input
                        type="text"
                        placeholder="Hierarchy name"
                        value={newHierarchyName}
                        onChange={(e) => setNewHierarchyName(e.target.value)}
                    />

                    <div className="hierarchy-builder-levels">
                        {newHierarchyLevels.map((level, i) => (
                            <span key={level} className="hierarchy-level-chip">
                                {levelLabel(level)}
                                <button onClick={() => handleMoveLevel(i, -1)} disabled={i === 0}>
                                    <FiArrowUp />
                                </button>
                                <button
                                    onClick={() => handleMoveLevel(i, 1)}
                                    disabled={i === newHierarchyLevels.length - 1}
                                >
                                    <FiArrowDown />
                                </button>
                                <button onClick={() => handleRemoveLevel(level)}>
                                    <FiX />
                                </button>
                            </span>
                        ))}
                    </div>

                    <div className="hierarchy-builder-add">
                        <select value={levelToAdd} onChange={(e) => setLevelToAdd(e.target.value)}>
                            <option value="">Add level...</option>
                            {AVAILABLE_LEVELS.filter(
                                (l) => !newHierarchyLevels.includes(l.value)
                            ).map((l) => (
                                <option key={l.value} value={l.value}>
                                    {l.label}
                                </option>
                            ))}
                        </select>
                        <button onClick={handleAddLevel}>Add Level</button>
                    </div>

                    <button
                        className="hierarchy-create-btn"
                        onClick={handleCreateHierarchy}
                        disabled={!newHierarchyName.trim() || newHierarchyLevels.length === 0}
                    >
                        Save Hierarchy
                    </button>
                </div>

            </div>
        </div>
    );
};

export default AttributeDefaultsSettings;